import { Check, X } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/NewButton";

type InlineTextEditorProps = {
  /** Value the editor starts with. */
  initialValue: string;
  /** Called on Enter, blur, or Check button. Parent is responsible for closing the editor
   *  on success (by flipping its own `editing` state). Don't close on failure to let the user retry. */
  onSubmit: (text: string) => void;
  /** Called on Escape or X button. Parent closes the editor. */
  onCancel: () => void;
  placeholder?: string;
  /** Classes on the Input (use to match the surrounding row's sizing, e.g. "h-7 text-sm flex-1"). */
  inputClassName?: string;
  /** Size of the Check/X commit/cancel icon buttons. Default "icon-md" (16px) matches a h-7 input;
   *  pass "icon-sm" (14px) for tighter rows with an h-6 input. */
  iconSize?: 'icon-sm' | 'icon-md';
};

/** Shared inline editor: an autoFocused Input + Check/X commit/cancel buttons.
 *
 *  The parent keeps the `editing` boolean and renders this component in place of the display label
 *  while editing. We own the draft state and the keyboard/blur handling so callers don't duplicate
 *  it. The parent's `onSubmit` / `onCancel` callbacks are what actually close the editor.
 *
 *  Implementation notes:
 *   - `stopPropagation` on keyDown/pointerDown stops drag handles and global shortcuts from
 *     intercepting keystrokes while the user is typing.
 *   - `onMouseDown` preventDefault on the Check/X buttons keeps the Input from blurring before
 *     their onClick fires — otherwise blur would commit and the button click would be a no-op. */
export const InlineTextEditor = ({
  initialValue,
  onSubmit,
  onCancel,
  placeholder,
  inputClassName,
  iconSize = 'icon-md',
}: InlineTextEditorProps) => {
  const [draft, setDraft] = useState(initialValue);

  return (
    <div
      className="flex items-center gap-1 flex-1"
      onKeyDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit(draft);
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => onSubmit(draft)}
        className={inputClassName}
        placeholder={placeholder}
        autoFocus
      />
      <Button
        icon={Check}
        variant="bare"
        size={iconSize}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSubmit(draft)}
        className="text-green-600 hover:text-green-800 shrink-0"
      />
      <Button
        icon={X}
        variant="bare"
        size={iconSize}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel}
        className="text-gray-400 hover:text-gray-600 shrink-0"
      />
    </div>
  );
};
